import React from "react";
import { CheckboxControl } from "common/components/Checkbox/CheckboxControl";
import { IFieldBaseProps } from "common/components/Field";
import { FormContext } from "common/components/Form/FormContext";
import { useDispatch } from "react-redux";
import { parsePath } from "common/helpers/FormPath";
import { getPrettyName } from "common/helpers/Pretty";
import { useAppSelector } from "common/hooks/storeHooks";
import { useValidate } from "common/components/Form/hooks/useValidate";
import { List } from "immutable";
import { Nullable } from "common/types";
import { P } from "common/components/Paragraph";

export interface ICheckboxGroupProps extends Omit<IFieldBaseProps<"checkboxGroup">, "disabled"> {
  size?: "sm" | "md";
  label: string;
  options: readonly string[];
  disabled?: (option: string) => boolean;
  classNameProp?: string;
  variant?: "row" | "col";
  namespace?: string;
  onChange?: (selectedValues: List<string>) => void;
  validate?: (value: List<string>, rootRecord?: any) => Nullable<string>;
}

export const CheckboxGroup: React.FC<ICheckboxGroupProps> = ({
  id,
  disabled,
  name,
  type,
  size = "md",
  options,
  classNameProp,
  variant = "col",
  namespace = "",
  onChange,
  validate,
}) => {
  // Constants
  const idName = id || name;

  // Hooks
  const { rootPath, updateFormReducer, errors } = React.useContext(FormContext);
  const dispatch = useDispatch();
  useValidate(validate, name);

  // Selectors
  const path = parsePath(name);
  const selectedValues: List<string> = useAppSelector((state) => List(state.getIn(rootPath.concat(path)) || []));

  // Methods
  const handleInput = (checked: boolean, option: string) => {
    let updatedSelectedValues: List<string>;
    if (checked) {
      const newSelectedValues = selectedValues.push(option);

      // Sort based on the original options order
      updatedSelectedValues = newSelectedValues.sort((a, b) => {
        const aIndex = options.indexOf(a);
        const bIndex = options.indexOf(b);
        return aIndex - bIndex;
      });
    } else {
      updatedSelectedValues = selectedValues.filter((value) => value !== option) as List<string>;
    }

    dispatch(
      updateFormReducer({
        rootPath,
        name,
        value: updatedSelectedValues,
      }),
    );
    onChange?.(updatedSelectedValues);
  };

  const isChecked = (option: string) => {
    return selectedValues.includes(option);
  };

  const ownClassName = variant === "col" ? "flex-col" : "flex flex-wrap";
  const errorClassName = errors.get(name) ? "border border-error-400 rounded-lg p-sm bg-error-50/20" : "";

  // Lifecycle
  return (
    <div className="flex flex-col gap-xs w-full">
      {/* CheckboxGroup container with error styling */}
      <div id={id} className={`${classNameProp} ${ownClassName} ${errorClassName}`}>
        {options.map((option) => (
          <CheckboxControl
            key={option}
            type={type}
            namespace={idName}
            label={getPrettyName(namespace ? namespace + "." + option : option)}
            isChecked={isChecked(option)}
            name={option}
            disabled={disabled && disabled(option)}
            size={size}
            onChange={handleInput}></CheckboxControl>
        ))}
      </div>

      {/* Error message */}
      {errors.get(name) && (
        <P className="text-error-400 leading-[16px]" size="sm">
          {errors.get(name)}
        </P>
      )}
    </div>
  );
};
